import { create } from 'zustand'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export interface DemoRequest {
  id: string
  firstName: string
  lastName: string
  email: string
  company: string
  phone: string | null
  message: string | null
  status: 'pending' | 'contacted' | 'converted' | 'rejected'
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface OrganizationSummary {
  id: string
  name: string
  userCount: number
  bidCount: number
  documentCount: number
  isActive: boolean
  lastActiveAt: string | null
  createdAt: string
}

/**
 * Per-organisation health over a trailing window.
 *
 * The six `funnel` keys are NOT commensurable and NOT narrowing stages — they
 * are counted in three different units (raw activity events, distinct bids for
 * `requirementsGenerated`, approved plan rows for `planApproved`). Render them
 * as independent counters; never as a funnel chart or stage percentages.
 *
 * `agentSpendUsd` covers agent-turn spend only — non-agent LLM calls are
 * unattributed until the cost-attribution work lands. Label it "Agent spend".
 */
export interface OrganizationHealth {
  lastActiveAt: string | null
  activeUsers: number
  activeDays: number
  bidsInFlight: number
  bidsSubmitted: number
  funnel: {
    documentUploaded: number
    requirementsGenerated: number
    planApproved: number
    draftCompleted: number
    reviewApproved: number
    artifactExported: number
  }
  feedback: { positive: number; negative: number }
  aiActivity: {
    plansGenerated: number
    draftsGenerated: number
    goNoGoScoresApplied: number
  }
  agentSpendUsd: number
}

export interface DashboardStats {
  pendingDemoRequests: number
  totalOrganizations: number
  activeOrganizations: number
  totalUsers: number
}

interface CreateOrgResult {
  organization: { id: string; name: string }
  invitation: { id: string; email: string }
  inviteUrl: string
}

export interface OrgUser {
  id: string
  email: string
  fullName: string | null
  avatarUrl: string | null
  role: 'admin' | 'member' | 'viewer'
  createdAt: string
  lastActiveAt: string | null
}

export interface OrgInvitation {
  id: string
  email: string
  role: 'admin' | 'member' | 'viewer'
  status: string
  expiresAt: string
  createdAt: string
}

export interface AdminUsageRow {
  day: string
  organizationId: string
  feature: string | null
  eventType: string
  costUsd: number
  inputTokens: number
  outputTokens: number
  pages: number
  quantity: number
  eventCount: number
}

// ===================
// Store
// ===================

interface AdminState {
  // Dashboard
  dashboardStats: DashboardStats | null
  recentDemoRequests: DemoRequest[]
  recentOrganizations: OrganizationSummary[]
  isDashboardLoading: boolean

  // Demo Requests
  demoRequests: DemoRequest[]
  isDemoRequestsLoading: boolean
  demoRequestsFilter: string | undefined

  // Organizations
  organizations: OrganizationSummary[]
  isOrganizationsLoading: boolean

  // Org Users (expanded detail)
  orgUsers: Record<string, OrgUser[]>
  orgUsersLoading: Record<string, boolean>

  // Org Invitations (expanded detail)
  orgInvitations: Record<string, OrgInvitation[]>
  orgInvitationsLoading: Record<string, boolean>

  // Usage metering
  usageRows: AdminUsageRow[]
  usageOrgs: Array<{ id: string; name: string }>
  isUsageLoading: boolean

  // Org Health (expanded detail)
  orgHealth: Record<string, OrganizationHealth>
  orgHealthLoading: string | null
  // Per-org failure message so the panel can distinguish "failed" from "no data"
  orgHealthError: Record<string, string>

  // Error
  error: string | null

  // Actions
  loadDashboard: () => Promise<void>
  loadDemoRequests: (status?: string) => Promise<void>
  updateDemoRequestStatus: (id: string, status: string, notes?: string) => Promise<void>
  convertDemoRequest: (id: string, organizationName: string, adminEmail: string, role?: string) => Promise<CreateOrgResult>
  loadOrganizations: () => Promise<void>
  createOrganization: (organizationName: string, adminEmail: string, role?: string) => Promise<CreateOrgResult>
  inviteUserToOrg: (organizationId: string, email: string, role?: string) => Promise<{ inviteUrl: string }>
  loadOrgUsers: (organizationId: string) => Promise<void>
  loadOrgInvitations: (organizationId: string) => Promise<void>
  fetchOrgHealth: (organizationId: string) => Promise<void>
  resendInvitation: (organizationId: string, invitationId: string) => Promise<void>
  toggleOrgActive: (id: string, isActive: boolean) => Promise<void>
  softDeleteOrg: (id: string) => Promise<void>
  loadUsage: (from?: string, to?: string) => Promise<void>
  clearError: () => void
}

interface DashboardResponse {
  demoRequests: { pending: number; total: number }
  organizations: { total: number; active: number }
  users: { total: number }
  recentDemoRequests: DemoRequest[]
  recentOrganizations: OrganizationSummary[]
}

function omitKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record
  const next = { ...record }
  delete next[key]
  return next
}

export const useAdminStore = create<AdminState>()((set, get) => ({
  // Initial state
  dashboardStats: null,
  recentDemoRequests: [],
  recentOrganizations: [],
  isDashboardLoading: false,
  demoRequests: [],
  isDemoRequestsLoading: false,
  demoRequestsFilter: undefined,
  organizations: [],
  isOrganizationsLoading: false,
  orgUsers: {},
  orgUsersLoading: {},
  orgInvitations: {},
  orgInvitationsLoading: {},
  usageRows: [],
  usageOrgs: [],
  isUsageLoading: false,
  orgHealth: {},
  orgHealthLoading: null,
  orgHealthError: {},
  error: null,

  loadDashboard: async () => {
    try {
      set({ isDashboardLoading: true, error: null })
      const data = await api.get<DashboardResponse>('/admin/dashboard')
      set({
        dashboardStats: {
          pendingDemoRequests: data.demoRequests.pending,
          totalOrganizations: data.organizations.total,
          activeOrganizations: data.organizations.active,
          totalUsers: data.users.total,
        },
        recentDemoRequests: data.recentDemoRequests,
        recentOrganizations: data.recentOrganizations,
        isDashboardLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load dashboard',
        isDashboardLoading: false,
      })
    }
  },

  loadDemoRequests: async (status?: string) => {
    try {
      set({ isDemoRequestsLoading: true, error: null, demoRequestsFilter: status })
      const params = status ? { status } : undefined
      const data = await api.get<{ demoRequests: DemoRequest[] }>('/admin/demo-requests', params)
      set({
        demoRequests: data.demoRequests,
        isDemoRequestsLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load demo requests',
        isDemoRequestsLoading: false,
      })
    }
  },

  updateDemoRequestStatus: async (id: string, status: string, notes?: string) => {
    try {
      set({ error: null })
      const data = await api.patch<{ demoRequest: DemoRequest }>(`/admin/demo-requests/${id}`, { status, notes })
      const updated = data.demoRequest
      set((state) => ({
        demoRequests: state.demoRequests.map((r) => (r.id === id ? updated : r)),
        recentDemoRequests: state.recentDemoRequests.map((r) => (r.id === id ? updated : r)),
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update demo request' })
      throw error
    }
  },

  convertDemoRequest: async (id: string, organizationName: string, adminEmail: string, role: string = 'admin') => {
    try {
      set({ error: null })
      const result = await api.post<CreateOrgResult & { demoRequest: { id: string; status: string } }>(
        `/admin/demo-requests/${id}/convert`,
        { organizationName, adminEmail, role }
      )
      // Update local state
      set((state) => ({
        demoRequests: state.demoRequests.map((r) =>
          r.id === id ? { ...r, status: 'converted' as const } : r
        ),
        recentDemoRequests: state.recentDemoRequests.map((r) =>
          r.id === id ? { ...r, status: 'converted' as const } : r
        ),
      }))
      return result
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to convert demo request' })
      throw error
    }
  },

  loadOrganizations: async () => {
    try {
      set({ isOrganizationsLoading: true, error: null })
      const data = await api.get<{ organizations: OrganizationSummary[] }>('/admin/organizations')
      set({
        organizations: data.organizations,
        isOrganizationsLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load organisations',
        isOrganizationsLoading: false,
      })
    }
  },

  createOrganization: async (organizationName: string, adminEmail: string, role: string = 'admin') => {
    try {
      set({ error: null })
      const result = await api.post<CreateOrgResult>('/admin/organizations', { organizationName, adminEmail, role })
      // Reload organizations to include the new one
      void get().loadOrganizations()
      return result
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create organisation' })
      throw error
    }
  },

  inviteUserToOrg: async (organizationId: string, email: string, role: string = 'member') => {
    try {
      set({ error: null })
      const result = await api.post<{ invitation: { id: string; email: string; role: string }; inviteUrl: string }>(
        `/admin/organizations/${organizationId}/invitations`,
        { email, role }
      )
      // Reload invitations so the new one shows up
      void get().loadOrgInvitations(organizationId)
      return { inviteUrl: result.inviteUrl }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to invite user' })
      throw error
    }
  },

  loadOrgUsers: async (organizationId: string) => {
    try {
      set((state) => ({ orgUsersLoading: { ...state.orgUsersLoading, [organizationId]: true } }))
      const data = await api.get<{ users: OrgUser[] }>(`/admin/organizations/${organizationId}/users`)
      set((state) => ({
        orgUsers: { ...state.orgUsers, [organizationId]: data.users },
        orgUsersLoading: { ...state.orgUsersLoading, [organizationId]: false },
      }))
    } catch (error) {
      set((state) => ({
        error: error instanceof Error ? error.message : 'Failed to load users',
        orgUsersLoading: { ...state.orgUsersLoading, [organizationId]: false },
      }))
    }
  },

  loadOrgInvitations: async (organizationId: string) => {
    try {
      set((state) => ({ orgInvitationsLoading: { ...state.orgInvitationsLoading, [organizationId]: true } }))
      const data = await api.get<{ invitations: OrgInvitation[] }>(`/admin/organizations/${organizationId}/invitations`)
      set((state) => ({
        orgInvitations: { ...state.orgInvitations, [organizationId]: data.invitations },
        orgInvitationsLoading: { ...state.orgInvitationsLoading, [organizationId]: false },
      }))
    } catch (error) {
      set((state) => ({
        error: error instanceof Error ? error.message : 'Failed to load invitations',
        orgInvitationsLoading: { ...state.orgInvitationsLoading, [organizationId]: false },
      }))
    }
  },

  fetchOrgHealth: async (organizationId: string) => {
    try {
      // Clear any previous failure so a retry starts from a clean slate
      set((state) => ({
        orgHealthLoading: organizationId,
        orgHealthError: omitKey(state.orgHealthError, organizationId),
        error: null,
      }))
      // Health rides the org resource as `?view=health` — API Gateway caps a
      // REST API at 300 resources and a dedicated path tipped us over.
      const health = await api.get<OrganizationHealth>(
        `/admin/organizations/${organizationId}`,
        { view: 'health', days: '30' }
      )
      set((state) => ({
        orgHealth: { ...state.orgHealth, [organizationId]: health },
        orgHealthError: omitKey(state.orgHealthError, organizationId),
        // Only clear the flag if a later request hasn't claimed it
        orgHealthLoading: state.orgHealthLoading === organizationId ? null : state.orgHealthLoading,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load organisation health'
      set((state) => ({
        error: message,
        orgHealthError: { ...state.orgHealthError, [organizationId]: message },
        orgHealthLoading: state.orgHealthLoading === organizationId ? null : state.orgHealthLoading,
      }))
    }
  },

  resendInvitation: async (organizationId: string, invitationId: string) => {
    try {
      set({ error: null })
      const data = await api.post<{ invitation: OrgInvitation }>(
        `/admin/organizations/${organizationId}/invitations/${invitationId}/resend`
      )
      // Replace the old invitation with the new one in local state
      set((state) => ({
        orgInvitations: {
          ...state.orgInvitations,
          [organizationId]: (state.orgInvitations[organizationId] ?? [])
            .filter((inv) => inv.id !== invitationId)
            .concat(data.invitation),
        },
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to resend invitation' })
      throw error
    }
  },

  toggleOrgActive: async (id: string, isActive: boolean) => {
    try {
      set({ error: null })
      await api.patch<{ organization: OrganizationSummary }>(`/admin/organizations/${id}`, { isActive })
      // Update local state
      set((state) => ({
        organizations: state.organizations.map((o) =>
          o.id === id ? { ...o, isActive } : o
        ),
        recentOrganizations: state.recentOrganizations.map((o) =>
          o.id === id ? { ...o, isActive } : o
        ),
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update organisation' })
      throw error
    }
  },

  softDeleteOrg: async (id: string) => {
    try {
      set({ error: null })
      await api.delete(`/admin/organizations/${id}`)
      set((state) => ({
        organizations: state.organizations.filter((o) => o.id !== id),
        recentOrganizations: state.recentOrganizations.filter((o) => o.id !== id),
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete organisation' })
      throw error
    }
  },

  loadUsage: async (from?: string, to?: string) => {
    try {
      set({ isUsageLoading: true, error: null })
      // Usage rides the dashboard resource as `?view=usage` — API Gateway caps a
      // REST API at 300 resources and a dedicated path tipped us over.
      const params: Record<string, string> = { view: 'usage' }
      if (from) params['from'] = from
      if (to) params['to'] = to
      const data = await api.get<{
        rows: AdminUsageRow[]
        organizations: Array<{ id: string; name: string }>
      }>('/admin/dashboard', params)
      set({ usageRows: data.rows, usageOrgs: data.organizations, isUsageLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load usage',
        isUsageLoading: false,
      })
    }
  },

  clearError: () => set({ error: null }),
}))
